import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SiGoogle } from "react-icons/si";
import { HiOutlineUser, HiOutlineMail, HiOutlineLockClosed, HiOutlineAcademicCap, HiOutlineShieldCheck, HiOutlineOfficeBuilding, HiOutlineSearch } from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ThreeDots } from "react-loader-spinner";

const SignUp = () => {
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState("");
  const [tempId, setTempId] = useState("");
  const [uniqueID, setUniqueId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  // New state for college selection
  const [colleges, setColleges] = useState([]);
  const [filteredColleges, setFilteredColleges] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [loadingColleges, setLoadingColleges] = useState(false);
  
  // State for "Other" college option
  const [showOtherCollege, setShowOtherCollege] = useState(false);
  const [otherCollegeName, setOtherCollegeName] = useState("");
  
  const navigate = useNavigate();

  // Fetch colleges after email verification
  useEffect(() => {
    if (uniqueID) {
      fetchColleges();
    }
  }, [uniqueID]);

  // Filter colleges based on search
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredColleges(colleges);
    } else {
      const filtered = colleges.filter(college =>
        college.collegeName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredColleges(filtered);
    }
  }, [searchTerm, colleges]);

  const fetchColleges = async () => {
    setLoadingColleges(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/apis/user/getAllColleges`,
      );
      setColleges(response.data.data || []);
      setFilteredColleges(response.data.data || []);
    } catch (err) {
      console.error("Error fetching colleges:", err);
    } finally {
      setLoadingColleges(false);
    }
  };

  const handleSendOtp = async () => {
    if (!email) {
      alert("Please enter your email first");
      return;
    }

    setIsLoading(true);
    setError("");
    console.log("Email: ", email);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/apis/userAuth/registerSendOtp`,
        { email: email },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      console.log(email);

      console.log("OTP Response:", response);

      if (response.data && response.data.data.tempId) {
        setTempId(response.data.data.tempId);
        setShowEmailVerification(true);
      }
    } catch (err) {
      console.error("OTP Error:", err);
      if (err.response) {
        if (err.response.status === 404) {
          setError("API endpoint not found. Please check the URL.");
        } else {
          setError(err.response.data?.message || "Failed to send OTP");
        }
      } else if (err.request) {
        setError("No response from server. Check your network connection.");
      } else {
        setError("Error setting up request: " + err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };
  console.log("TempId: ", tempId);

  const handleVerifyEmail = async () => {
    if (!emailVerificationCode) {
      alert("Please enter the verification code.");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/apis/userAuth/verifyOtp`,
        {
          tempId: tempId,
          otp: emailVerificationCode
        }
      );

      console.log("Verification Response:", response.data);

      if (response.data.success && response.data.data.uniqueId) {
        setUniqueId(response.data.data.uniqueId);
        setShowEmailVerification(false);
      } else {
        setError(response.data.message || "Verification failed");
      }
    } catch (err) {
      console.error("Verification Error:", err);
      if (err.response) {
        setError(err.response.data?.message || "Failed to verify OTP");
      } else {
        setError("Network error. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  console.log("UniqueId: ", uniqueID);

  const handleSignUp = async (e) => {
    e.preventDefault();
    console.log("🚀 SUBMITTING REGISTRATION");
    console.log("🚀 uniqueId from state:", uniqueID);

    if (password !== confirmPassword) {
      alert("Passwords do not match!");
      return;
    }

    if (!uniqueID) {
      alert("Please verify your email first");
      return;
    }

    // Validate college selection
    if (!selectedCollege && !showOtherCollege) {
      alert("Please select your college or choose 'Other'");
      return;
    }

    if (showOtherCollege && !otherCollegeName.trim()) {
      alert("Please enter your college name");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      // Prepare request data based on college selection
      const requestData = {
        fullName: name,
        uniqueId: uniqueID,
        password: password
      };

      // If "Other" college is selected, send collegeName instead of collegeId
      if (showOtherCollege) {
        requestData.collegeName = otherCollegeName.trim();
      } else {
        requestData.collegeId = selectedCollege._id;
      }

      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/apis/userAuth/register`, requestData);

      navigate("/fill-information");
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle "Other" option selection
  const handleOtherOption = () => {
    setShowOtherCollege(true);
    setSelectedCollege(null);
    setSearchTerm("");
    setDropdownOpen(false);
  };

  // Handle regular college selection
  const handleCollegeSelect = (college) => {
    setSelectedCollege(college);
    setSearchTerm(college.collegeName);
    setShowOtherCollege(false);
    setOtherCollegeName("");
    setDropdownOpen(false);
  };

  // Password strength checker
  const checkPasswordStrength = (pass) => {
    let strength = 0;
    if (pass.length >= 8) strength += 1;
    if (pass.match(/[a-z]+/)) strength += 1;
    if (pass.match(/[A-Z]+/)) strength += 1;
    if (pass.match(/[0-9]+/)) strength += 1;
    if (pass.match(/[$@#&!]+/)) strength += 1;
    setPasswordStrength(strength);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    checkPasswordStrength(newPassword);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Animated background elements - Light Gray */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-60"
          animate={{
            x: [0, -50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      </div>

      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center"
            >
              <ThreeDots height="60" width="60" color="#6b7280" ariaLabel="loading" />
              <p className="text-gray-700 font-semibold mt-4">Processing...</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content - Fixed Layout */}
      <div className="flex flex-col md:flex-row min-h-screen relative z-10">
        {/* Left Image Section - FIXED on desktop */}
        <div className="hidden md:block md:w-2/5 lg:w-1/3 fixed left-0 top-0 h-screen">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="w-full h-full relative overflow-hidden group"
          >
            <div className="w-full h-full bg-gradient-to-br from-gray-600 via-gray-700 to-gray-800 relative">
              {/* Animated overlay */}
              <motion.div
                className="absolute inset-0 bg-black opacity-20"
                animate={{
                  opacity: [0.2, 0.3, 0.2],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              />

              {/* Image with subtle zoom effect */}
              <motion.img
                src="assets/bg.png"
                alt="Signup Banner"
                className="w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-700"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.6 }}
              />

              {/* Floating academic cap */}
              <motion.div
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white"
                animate={{
                  y: [0, -15, 0],
                  rotate: [0, 5, -5, 0],
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                <HiOutlineAcademicCap className="w-24 h-24 opacity-20" />
              </motion.div>

              {/* Gradient overlay with welcome text - NOW FIXED at bottom */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="space-y-2"
                >
                  <h3 className="text-white text-3xl font-bold">Start Your Journey</h3>
                  <p className="text-white/90 text-lg">Join 10,000+ alumni already connected</p>
                  <div className="flex items-center space-x-4 mt-4">
                    <div className="flex -space-x-2">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="w-8 h-8 rounded-full bg-white/20 border-2 border-white/30"></div>
                      ))}
                    </div>
                    <p className="text-white/80 text-sm">500+ joining weekly</p>
                  </div>
                </motion.div>
              </div>

              {/* Floating stats badge */}
              <motion.div
                className="absolute top-8 right-8 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20"
                animate={{
                  x: [0, -10, 0],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                <p className="text-white text-xs">🎓 Class of 2024</p>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Mobile Left Section (visible only on mobile) */}
        <div className="md:hidden w-full h-64 relative overflow-hidden">
          <div className="w-full h-full bg-gradient-to-br from-gray-600 via-gray-700 to-gray-800 relative">
            <img
              src="assets/bg.png"
              alt="Signup Banner"
              className="w-full h-full object-cover opacity-70"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
              <h3 className="text-white text-2xl font-bold">Start Your Journey</h3>
              <p className="text-white/80">Join 10,000+ alumni</p>
            </div>
          </div>
        </div>

        {/* Right Form Section - SCROLLABLE */}
        <div className="w-full md:w-3/5 lg:w-2/3 md:ml-auto min-h-screen">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex items-center justify-center p-6 md:p-12 min-h-screen"
          >
            <div className="w-full max-w-md py-8">
              {/* Header with animation */}
              <motion.div variants={itemVariants} className="mb-8">
                <motion.h2
                  className="text-4xl font-bold text-gray-700 mb-3"
                >
                  Join Our Community
                </motion.h2>
                <motion.p
                  variants={itemVariants}
                  className="text-gray-600 text-lg"
                >
                  Create your account and connect with alumni worldwide
                </motion.p>
              </motion.div>

              {/* Error Message with Animation */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-start"
                  >
                    <span className="text-lg mr-3">⚠️</span>
                    <span className="text-sm font-medium">{error}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSignUp} className="space-y-5">
                {/* Full Name Field */}
                <motion.div variants={itemVariants} className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Full Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <HiOutlineUser className="h-5 w-5 text-gray-400" />
                    </div>
                    <motion.input
                      whileFocus={{ scale: 1.01 }}
                      type="text"
                      placeholder="John Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                      required
                    />
                  </div>
                </motion.div>

                {/* Email Field */}
                <motion.div variants={itemVariants} className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Email Address</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <HiOutlineMail className="h-5 w-5 text-gray-400" />
                    </div>
                    <motion.input
                      whileFocus={{ scale: 1.01 }}
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                      required
                    />
                  </div>
                </motion.div>

                {/* Send OTP Button */}
                {!uniqueID && (
                  <motion.div variants={itemVariants}>
                    <motion.button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={isLoading}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full bg-gray-700 hover:bg-gray-800 disabled:bg-gray-400 text-white font-semibold py-3 rounded-xl transition-all duration-300 shadow-md"
                    >
                      {isLoading ? "Sending OTP..." : "Send Verification Code"}
                    </motion.button>
                  </motion.div>
                )}

                {/* Email Verification Section */}
                <AnimatePresence>
                  {showEmailVerification && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-3 bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-xl border border-gray-200 overflow-hidden"
                    >
                      <div className="flex items-center space-x-2">
                        <HiOutlineShieldCheck className="w-5 h-5 text-gray-600" />
                        <label className="block text-sm font-semibold text-gray-700">Verification Code</label>
                      </div>
                      <motion.input
                        whileFocus={{ scale: 1.01 }}
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={emailVerificationCode}
                        onChange={(e) => setEmailVerificationCode(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                        required
                      />
                      <motion.button
                        type="button"
                        onClick={handleVerifyEmail}
                        disabled={isLoading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full bg-gray-700 hover:bg-gray-800 disabled:bg-gray-400 text-white font-semibold py-3 rounded-xl transition-all duration-200 shadow-md"
                      >
                        {isLoading ? "Verifying..." : "Verify Code"}
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* College Selection - Show after email verification */}
                {uniqueID && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-2"
                  >
                    <label className="block text-sm font-semibold text-gray-700">
                      Select Your College <span className="text-red-500">*</span>
                    </label>
                    
                    {/* Search Input */}
                    <div className="relative">
                      <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={() => setDropdownOpen(true)}
                        placeholder="Search for your college..."
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white"
                      />
                    </div>

                    {/* Dropdown */}
                    {dropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute z-50 w-full max-w-md mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto"
                      >
                        {loadingColleges ? (
                          <div className="p-4 text-center text-gray-500">Loading colleges...</div>
                        ) : (
                          <>
                            {/* "Other" Option */}
                            <button
                              type="button"
                              onClick={handleOtherOption}
                              className="w-full text-left px-4 py-3 hover:bg-amber-50 transition-colors flex items-center space-x-3 border-b border-gray-100 bg-amber-50/50"
                            >
                              <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                                <span className="text-white text-xs">+</span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-amber-700">Other (My college is not listed)</p>
                                <p className="text-xs text-gray-500">Select this if your college is not in the list</p>
                              </div>
                            </button>

                            {/* Regular College Options */}
                            {filteredColleges.length > 0 ? (
                              filteredColleges.map((college) => (
                                <button
                                  key={college._id}
                                  type="button"
                                  onClick={() => handleCollegeSelect(college)}
                                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center space-x-3 border-b border-gray-100 last:border-0"
                                >
                                  <HiOutlineOfficeBuilding className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium text-gray-800 truncate">{college.collegeName}</p>
                                    {college.address && (
                                      <p className="text-xs text-gray-500 truncate">{college.address}</p>
                                    )}
                                  </div>
                                </button>
                              ))
                            ) : (
                              <div className="p-4 text-center text-gray-500">
                                No colleges found.{" "}
                                <button
                                  onClick={handleOtherOption}
                                  className="text-amber-600 hover:text-amber-700 font-medium"
                                >
                                  Add your college
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </motion.div>
                    )}

                    {/* Selected College or Other College Input */}
                    <AnimatePresence mode="wait">
                      {selectedCollege && (
                        <motion.div
                          key="selected"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200 flex items-center space-x-2"
                        >
                          <HiOutlineOfficeBuilding className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-green-700 font-medium">
                            Selected: {selectedCollege.collegeName}
                          </span>
                        </motion.div>
                      )}

                      {showOtherCollege && (
                        <motion.div
                          key="other"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-3 space-y-2"
                        >
                          <label className="block text-sm font-semibold text-gray-700">
                            Enter Your College Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={otherCollegeName}
                            onChange={(e) => setOtherCollegeName(e.target.value)}
                            placeholder="e.g., Stanford University"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            required={showOtherCollege}
                          />
                          <p className="text-xs text-gray-500 flex items-center space-x-1">
                            <span className="inline-block w-4 h-4 bg-amber-100 rounded-full text-amber-600 text-center">i</span>
                            <span>Your college will be added to our database after verification</span>
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}

                {/* Password Field */}
                <motion.div variants={itemVariants} className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <HiOutlineLockClosed className="h-5 w-5 text-gray-400" />
                    </div>
                    <motion.input
                      whileFocus={{ scale: 1.01 }}
                      type="password"
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={handlePasswordChange}
                      className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                      required
                    />
                  </div>

                  {/* Password Strength Indicator */}
                  {password && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2"
                    >
                      <div className="flex space-x-1">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <motion.div
                            key={level}
                            className={`h-1 flex-1 rounded-full ${level <= passwordStrength
                                ? level <= 2 ? 'bg-red-400' : level <= 4 ? 'bg-yellow-400' : 'bg-green-400'
                                : 'bg-gray-200'
                              }`}
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ delay: level * 0.1 }}
                          />
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {passwordStrength === 0 && "Very weak"}
                        {passwordStrength === 1 && "Weak"}
                        {passwordStrength === 2 && "Fair"}
                        {passwordStrength === 3 && "Good"}
                        {passwordStrength === 4 && "Strong"}
                        {passwordStrength === 5 && "Very strong"}
                      </p>
                    </motion.div>
                  )}
                </motion.div>

                {/* Confirm Password Field */}
                <motion.div variants={itemVariants} className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Confirm Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <HiOutlineLockClosed className="h-5 w-5 text-gray-400" />
                    </div>
                    <motion.input
                      whileFocus={{ scale: 1.01 }}
                      type="password"
                      placeholder="Re-enter your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 ${confirmPassword && password !== confirmPassword
                          ? 'border-red-300 bg-red-50'
                          : confirmPassword && password === confirmPassword
                            ? 'border-green-300 bg-green-50'
                            : 'border-gray-200'
                        }`}
                      required
                    />
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-xs text-red-500 mt-1"
                    >
                      Passwords do not match
                    </motion.p>
                  )}
                  {confirmPassword && password === confirmPassword && password.length > 0 && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-xs text-green-500 mt-1"
                    >
                      ✓ Passwords match
                    </motion.p>
                  )}
                </motion.div>

                {/* Create Account Button */}
                <motion.div variants={itemVariants}>
                  <motion.button
                    type="submit"
                    disabled={isLoading || (!selectedCollege && !showOtherCollege) || (showOtherCollege && !otherCollegeName.trim())}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-gray-700 text-white text-lg font-semibold py-3 rounded-xl hover:bg-gray-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                  >
                    {isLoading ? "Creating Account..." : "Create Account"}
                  </motion.button>
                </motion.div>

                {/* Login Link */}
                <motion.p
                  variants={itemVariants}
                  className="text-center text-gray-600 mt-6"
                >
                  Already a member?{" "}
                  <motion.span
                    onClick={() => navigate("/login")}
                    className="font-semibold text-gray-700 cursor-pointer inline-block hover:text-gray-900"
                    whileHover={{ scale: 1.05, x: 2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Log in
                  </motion.span>
                </motion.p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;